import { Button } from '@creditornot/cb-button';
import { useMutation } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { updateDraftVenue } from 'apis/merchant-onboarding-service/api';
import { UpdateDraftVenueRequest } from 'apis/merchant-onboarding-service/types';
import { APIErrorNotification } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import useDraftVenue from '../../DraftVenueListPage/useDraftVenue';
import Confirm from '../Confirm';
import { draftVenueFormValuesToApi, fromApiToDraftVenueFormValues } from '../transformers';
import { DraftVenueFormValues } from '../types';
import validationSchema from '../validation/validationSchema';

type Props = {
  className?: string;
  onSaveSuccess: () => void;
};

const Root = styled.div``;

const Actions = styled.div`
  margin-block-start: 16px;
`;

const PrimaryAction = styled(Button).attrs({
  size: 'large',
  variant: 'primary',
  stretch: true,
})`
  margin-block-end: 8px;
`;

const SecondaryAction = styled(Button).attrs({
  size: 'large',
  variant: 'secondary',
  stretch: true,
})``;

const SubmitError = styled(APIErrorNotification)`
  margin-block-start: 8px;
`;

const DraftVenueEditorConfirm = ({ className, onSaveSuccess }: Props) => {
  const history = useHistory();
  const { merchantId, venueId } = useParams<{ merchantId: string; venueId: string }>();
  const { data: draftVenue, error: draftVenueError } = useDraftVenue(merchantId, venueId);
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useMutation<unknown, Error, { payload: UpdateDraftVenueRequest; exit: boolean }>(
    ({ payload }) => updateDraftVenue(merchantId, venueId, payload),
    {
      onSuccess: (_, { exit }) => {
        if (exit) {
          return history.push(`/venue-data-collection/${merchantId}`);
        }
        return onSaveSuccess();
      },
    },
  );
  const initialFormValues = useMemo(() => draftVenue && fromApiToDraftVenueFormValues(draftVenue), [draftVenue]);
  return (
    <Root className={className}>
      {draftVenueError ? (
        <APIErrorNotification error={draftVenueError} />
      ) : (
        initialFormValues &&
        draftVenue && (
          <Form<DraftVenueFormValues>
            initialValues={initialFormValues}
            validate={validateYupSchema(validationSchema)}
            onSubmit={values => {
              mutate({
                payload: {
                  ...draftVenueFormValuesToApi(validationSchema.validateSync(values)),
                  status: 'READY_TO_SUBMIT',
                },
                exit: false,
              });
            }}
          >
            {({ handleSubmit, pristine, values, hasValidationErrors }) => {
              const handleSaveAndExit = () => {
                mutate({
                  payload: {
                    ...draftVenueFormValuesToApi(validationSchema.validateSync(values)),
                    status: 'BLANK',
                  },
                  exit: true,
                });
              };
              return (
                <form onSubmit={handleSubmit}>
                  <Confirm />
                  <Actions>
                    <PrimaryAction
                      data-test-id={'draft-venue-editor-confirm-button'}
                      type="submit"
                      loading={isSaving}
                      disabled={hasValidationErrors || (pristine && values.status === 'READY_TO_SUBMIT')}
                    >
                      <FormattedMessage id="venue-data-collection.venue-information-form.confirm" />
                    </PrimaryAction>
                    <SecondaryAction
                      data-test-id={'draft-venue-editor-save-and-exit-button'}
                      type="button"
                      disabled={pristine}
                      loading={isSaving}
                      onClick={handleSaveAndExit}
                    >
                      <FormattedMessage id="venue-data-collection.venue-information-form.saveAndExit" />
                    </SecondaryAction>
                  </Actions>
                  {saveError && <SubmitError error={saveError} />}
                </form>
              );
            }}
          </Form>
        )
      )}
    </Root>
  );
};

export default DraftVenueEditorConfirm;
